import { Plus, Sun } from "lucide-react";
import { eur, fileUrl } from "@/lib/api";

export function DailySpecials({ specials, t, theme, onAdd, hero = false, onBrowse }) {
  if (!specials?.length) return null;
  if (hero) {
    return (
      <section data-testid="daily-specials-hero" className="mb-2 space-y-4">
        {specials.map((s) => {
          const off = s.sold_out;
          return (
            <article key={s.id} data-testid={`special-hero-${s.id}`} className={`overflow-hidden rounded-3xl border border-[#C69C4A]/50 bg-white shadow-[0_10px_32px_rgba(198,156,74,0.2)] ${off ? "opacity-80" : ""}`}>
              <div className="relative aspect-[4/3] max-h-[340px] bg-[#EFEADF]">
                {s.image ? <img src={fileUrl(s.image)} alt={s.name} className={`w-full h-full object-cover ${off ? "grayscale" : ""}`} />
                  : <div className="w-full h-full grid place-items-center font-serif-display text-6xl text-[#B69A6B]">{(s.name || "?").charAt(0)}</div>}
                <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[#7B1E24] text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow"><Sun className="w-3 h-3" /> {t.dailySpecialTag}</span>
                {off && <span className="absolute inset-0 bg-[#0A1C16]/65 grid place-items-center text-white text-sm font-bold">{t.unavailableNow}</span>}
              </div>
              <div className="p-4">
                <h2 className="font-serif-display text-2xl text-[#123026] font-semibold leading-tight">{s.name}</h2>
                {s.description && <p className="text-sm text-[#5C615E] mt-1.5 leading-relaxed">{s.description}</p>}
                <div className="flex items-center justify-between mt-4">
                  <span className="font-serif-display text-2xl font-bold text-[#7B1E24]">{eur(s.price)}</span>
                  {off ? <span className="text-xs font-semibold text-[#8A918D]">{t.unavailableNow}</span> : (
                    <button data-testid={`special-hero-add-${s.id}`} onClick={() => onAdd(s)} style={{ backgroundColor: theme.primary, color: theme.gold }} className="h-11 pl-3.5 pr-5 rounded-full flex items-center gap-1.5 text-sm font-bold hover:brightness-110 active:scale-95 transition-all shadow-md">
                      <Plus className="w-4 h-4" strokeWidth={2.5} /> {t.add}
                    </button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
        {onBrowse && <button data-testid="special-browse-menu" onClick={onBrowse} className="w-full text-sm font-semibold text-[#7B1E24] underline underline-offset-4 py-2">{t.viewMenu}</button>}
      </section>
    );
  }
  return (
    <section data-testid="daily-specials" className="mb-2">
      <div className="flex items-center gap-2 mb-3">
        <Sun className="w-4 h-4 text-[#C69C4A]" />
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#C69C4A] font-bold">{t.dailySpecial}</p>
        <div className="flex-1 h-px bg-gradient-to-r from-[#C69C4A]/60 via-[#C69C4A]/20 to-transparent" />
      </div>
      <div className="space-y-3">
        {specials.map((s) => {
          const off = s.sold_out;
          return (
            <div key={s.id} data-testid={`special-card-${s.id}`} className={`relative overflow-hidden rounded-3xl border border-[#C69C4A]/50 bg-gradient-to-br from-[#FAF3E0] to-white shadow-[0_8px_28px_rgba(198,156,74,0.18)] ${off ? "opacity-80" : ""}`}>
              <div className="flex gap-4 p-3.5 sm:p-4">
                <div className="relative w-28 h-28 sm:w-36 sm:h-36 shrink-0 rounded-2xl overflow-hidden bg-[#EFEADF]">
                  {s.image ? <img src={fileUrl(s.image)} alt={s.name} className={`w-full h-full object-cover ${off ? "grayscale" : ""}`} />
                    : <div className="w-full h-full grid place-items-center font-serif-display text-3xl text-[#B69A6B]">{(s.name || "?").charAt(0)}</div>}
                  <span className="absolute top-2 left-2 bg-[#7B1E24] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">{t.dailySpecialTag}</span>
                  {off && (
                    <span className="absolute inset-0 bg-[#0A1C16]/65 grid place-items-center text-center px-2">
                      <span data-testid={`special-soldout-${s.id}`} className="text-white text-[11px] font-bold leading-tight">{t.unavailableSuccess}<br /><span className="font-medium text-[10px] opacity-90">{t.unavailableNow}</span></span>
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0 flex flex-col py-0.5">
                  <p className="font-serif-display text-lg sm:text-xl text-[#123026] leading-snug font-semibold">{s.name}</p>
                  <p className="text-xs sm:text-sm text-[#5C615E] mt-1 line-clamp-3 leading-relaxed">{s.description}</p>
                  <div className="flex items-center justify-between mt-auto pt-2.5">
                    <span className="font-serif-display text-lg font-bold text-[#7B1E24]">{eur(s.price)}</span>
                    {off ? (
                      <span className="text-[11px] font-semibold text-[#8A918D]">{t.unavailableNow}</span>
                    ) : (
                      <button data-testid={`special-add-${s.id}`} onClick={() => onAdd(s)} style={{ backgroundColor: theme.primary, color: theme.gold }} className="h-10 pl-3 pr-4 rounded-full flex items-center gap-1.5 text-sm font-bold hover:brightness-110 active:scale-95 transition-all shadow-md">
                        <Plus className="w-4 h-4" strokeWidth={2.5} /> {t.add}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
